import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { LINKS, isConfigured } from "../../config/links";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const openApp = () => {
  if (isConfigured(LINKS.appUrl)) window.open(LINKS.appUrl, "_blank", "noopener");
  else toast.info("GoDiGo is launching soon", { description: "The app link will be live here the moment GoDiGo is published." });
};

export const Wordmark = ({ light = false }) => (
  <span className="inline-flex items-center gap-2 select-none">
    <span className="relative flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: light ? "#A3E635" : "#1B4D3E" }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5a6.5 6.5 0 1 0 6.5 6.5H8" stroke={light ? "#143B2F" : "#A3E635"} strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    </span>
    <span className={`font-heading text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
      GoDiGo<span style={{ color: light ? "#A3E635" : "#2E7D5B" }}>.</span>
    </span>
  </span>
);

const NAV = [
  { label: "Features", id: "features" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Privacy & Security", id: "privacy" },
  { label: "Support", id: "support" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="glass-nav fixed inset-x-0 top-0 z-50"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button data-testid="nav-logo" onClick={() => scrollToId("hero")} className="cursor-pointer">
          <Wordmark />
        </button>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => scrollToId(n.id)}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-emerald-900"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="nav-get-godigo-button" onClick={openApp} className="btn-primary hidden px-5 py-2.5 text-sm md:inline-flex">
            Get GoDiGo
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="rounded-lg p-2 text-slate-700 md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="border-t border-emerald-900/10 bg-white/95 px-4 pb-5 pt-3 md:hidden" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-mobile-link-${n.id}`}
              onClick={() => { setOpen(false); scrollToId(n.id); }}
              className="block w-full py-3 text-left text-base font-medium text-slate-700"
            >
              {n.label}
            </button>
          ))}
          <button data-testid="nav-mobile-get-godigo" onClick={() => { setOpen(false); openApp(); }} className="btn-primary mt-2 w-full px-5 py-3 text-sm">
            Get GoDiGo
          </button>
        </motion.div>
      )}
    </motion.header>
  );
}
